import { Folder } from 'lucide-react';
import EmptyState from '../../../shared/EmptyState';
import DataTable from '../../../shared/DataTable';
import TableSpinner from '../../../shared/TableSpinner';

const FolderTypeIcon = () => <Folder className="h-16 w-16" strokeWidth={1.5} />;

const FolderTypesTable = ({
  data,
  isLoading,
  isFetching,
  isError,
  error,
  onEdit,
  onDelete,
  isBusy = false,
  deletingId,
}) => {
  if (isLoading) {
    return <TableSpinner message="Loading folder types…" />;
  }

  if (isError) {
    const message =
      error?.response?.data?.message || error?.message || 'Unable to load folder types.';
    return (
      <EmptyState
        isError={true}
        title="Error loading folder types"
        description={message}
        icon={<FolderTypeIcon />}
      />
    );
  }

  if (!data?.length) {
    return (
      <EmptyState
        title="No folder types found"
        description="Adjust filters or create a folder type to see it listed here."
        icon={<FolderTypeIcon />}
      />
    );
  }

  const columns = [
    {
      key: 'code',
      header: 'Code',
      render: (row) => <span className="font-medium text-slate-700">{row.code ?? '--'}</span>,
    },
    {
      key: 'name',
      header: 'Name',
      render: (row) => <span className="text-slate-600">{row.name}</span>,
    },
    {
      key: 'description',
      header: 'Description',
      render: (row) => (
        <span className="max-w-[200px] truncate block text-slate-500" title={row.description}>
          {row.description || '--'}
        </span>
      ),
    },
    {
      key: 'isActive',
      header: 'Active',
      render: (row) => (
        <span
          className={`inline-flex rounded-full px-2.5 py-1 text-xs font-medium ${
            row.isActive ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
          }`}
        >
          {row.isActive ? 'Active' : 'Inactive'}
        </span>
      ),
    },
    {
      key: 'createdBy',
      header: 'Created By',
      render: (row) => (
        <span className="text-slate-500">
          {row.createdBy ? `${row.createdBy.firstName} ${row.createdBy.lastName}` : '--'}
        </span>
      ),
    },
    {
      key: 'actions',
      header: 'Actions',
      align: 'right',
      render: (folderType) => (
        <div className="flex justify-end gap-2">
          <button
            type="button"
            onClick={() => onEdit?.(folderType)}
            className="inline-flex items-center gap-1 rounded-lg border border-cream/60 bg-white px-3 py-1.5 text-xs font-semibold text-primary transition hover:bg-cream/60 disabled:cursor-not-allowed disabled:opacity-60"
            disabled={isBusy}
            aria-label={`Edit ${folderType.name}`}
          >
            <svg
              className="h-3.5 w-3.5"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M12 20h9" />
              <path d="M16.5 3.5a2.121 2.121 0 1 1 3 3L7 19l-4 1 1-4 12.5-12.5z" />
            </svg>
            Edit
          </button>
          <button
            type="button"
            onClick={() => onDelete?.(folderType)}
            className="inline-flex items-center gap-1 rounded-lg border border-red-100 bg-red-50 px-3 py-1.5 text-xs font-semibold text-red-600 transition hover:bg-red-100 disabled:cursor-not-allowed disabled:opacity-60"
            disabled={isBusy}
            aria-label={`Delete ${folderType.name}`}
          >
            {isBusy && deletingId === folderType._id ? (
              <span className="flex items-center gap-1">
                <svg
                  className="h-3 w-3 animate-spin"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M21 12a9 9 0 1 1-6.219-8.56" />
                </svg>
                Removing…
              </span>
            ) : (
              <>
                <svg
                  className="h-3.5 w-3.5"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <polyline points="3 6 5 6 21 6" />
                  <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6" />
                  <path d="M10 11v6" />
                  <path d="M14 11v6" />
                </svg>
                Delete
              </>
            )}
          </button>
        </div>
      ),
    },
  ];

  return (
    <DataTable
      columns={columns}
      rows={data}
      isLoading={isLoading}
      isFetching={isFetching}
      isError={isError}
      error={error}
      emptyTitle="No folder types found"
      emptyMessage="Adjust filters or create a folder type to see it listed here."
      emptyIcon={<FolderTypeIcon />}
    />
  );
};

export default FolderTypesTable;
